import api from './api';

import {
  DEFAULT_MUNICIPAL_SECTIONS,
  resolveSectionDefinitions,
} from '../utils/aiPromptConfigUtils';

const STORAGE_KEY = 'aiPromptConfigs';
const PLACEHOLDER_ID = 'placeholder-municipal-rfp';

let useLocalOnly = false;

const DEFAULT_PLACEHOLDER_PROMPTS = [
  {
    _id: PLACEHOLDER_ID,
    promptName: 'Municipal / Government RFP Prompt',
    promptContent: `Generate a complete municipal/government Request for Proposal (RFP).

Every response MUST include ALL of the following required sections. Do not omit any section, even if information is limited. When information is unavailable, generate reasonable placeholders and assumptions.

Required Sections:
1. Scope of Work — Detailed description of the work, services, or goods to be procured, including technical specifications, deliverables, and performance standards.
2. Eligibility Requirements — Minimum qualifications, registrations, certifications, financial thresholds, and experience criteria bidders must meet.
3. Evaluation Methodology — Criteria, weightage, and process for evaluating bids (e.g., QCBS, L1, technical scoring).
4. Payment Terms — Milestone-based payment schedule, retention amounts, advance payments, and invoicing procedures.
5. Project Timeline — Key dates, milestones, deliverables schedule, and overall contract period.
6. Penalty Clause — Late delivery penalties, SLA non-compliance penalties, performance liquidated damages, and maximum penalty caps.
7. Publishing Authority — The issuing authority name, department, contact details, address, and publication/reference details.

Organization Defaults:
Use organization defaults as authoritative context. Incorporate the organization name, department name, contact information, address, procurement policies, standard terms, legal clauses, payment preferences, and evaluation preferences throughout the RFP. When these values are present, use them in every applicable section.`,
    sectionMapping: DEFAULT_MUNICIPAL_SECTIONS,
    isActive: true,
    isPlaceholder: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

export const isPlaceholderPrompt = (prompt) =>
  Boolean(prompt?.isPlaceholder) || prompt?._id === PLACEHOLDER_ID;

const isPersistedPromptId = (id) =>
  typeof id === 'string' && /^[a-f\d]{24}$/i.test(id);

const normalizePrompt = (prompt = {}) => ({
  _id: prompt._id || String(Date.now() + Math.random()),
  promptName: prompt.promptName || '',
  promptContent: prompt.promptContent || '',
  sectionMapping: resolveSectionDefinitions(prompt),
  isActive: Boolean(prompt.isActive),
  isPlaceholder: isPlaceholderPrompt(prompt),
  createdAt: prompt.createdAt || new Date().toISOString(),
  updatedAt: prompt.updatedAt || new Date().toISOString(),
});

const getLocalPrompts = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const setLocalPrompts = (list) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
};

const extractApiError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const buildCreatePayload = (payload) => ({
  promptName: payload.promptName,
  promptContent: payload.promptContent,
  sectionMapping: payload.sectionMapping,
  isActive: Boolean(payload.isActive),
});

const buildUpdatePayload = (payload) => {
  const body = {};
  if (typeof payload.promptName !== 'undefined') body.promptName = payload.promptName;
  if (typeof payload.promptContent !== 'undefined') {
    body.promptContent = payload.promptContent;
  }
  if (typeof payload.sectionMapping !== 'undefined') {
    body.sectionMapping = payload.sectionMapping;
  }
  if (typeof payload.isActive !== 'undefined') body.isActive = Boolean(payload.isActive);
  return body;
};

class AiPromptConfigService {
  static async getPrompts() {
    if (useLocalOnly) {
      const local = getLocalPrompts().map(normalizePrompt);
      return {
        data: local.length ? local : DEFAULT_PLACEHOLDER_PROMPTS,
        hasPlaceholderOnly: local.length === 0,
      };
    }

    try {
      const response = await api.get('/sourcing/ai-prompts');
      const list = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      const normalized = list.map(normalizePrompt);
      return {
        data: normalized.length ? normalized : DEFAULT_PLACEHOLDER_PROMPTS,
        hasPlaceholderOnly: normalized.length === 0,
      };
    } catch (error) {
      if (error?.response?.status === 404) {
        useLocalOnly = true;
        const local = getLocalPrompts().map(normalizePrompt);
        return {
          data: local.length ? local : DEFAULT_PLACEHOLDER_PROMPTS,
          hasPlaceholderOnly: local.length === 0,
        };
      }
      throw new Error(
        extractApiError(error, 'Failed to load AI prompt configurations.'),
      );
    }
  }

  static async createPrompt(payload) {
    const body = buildCreatePayload(payload);

    if (useLocalOnly) {
      const prompt = normalizePrompt({ ...body, _id: String(Date.now()) });
      const current = getLocalPrompts().map(normalizePrompt);
      const next = body.isActive
        ? [prompt, ...current.map((item) => ({ ...item, isActive: false }))]
        : [prompt, ...current];
      setLocalPrompts(next);
      return { success: true, data: prompt };
    }

    try {
      const response = await api.post('/sourcing/ai-prompts', body);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        useLocalOnly = true;
        return this.createPrompt(payload);
      }
      throw new Error(extractApiError(error, 'Failed to create prompt.'));
    }
  }

  static async updatePrompt(id, payload) {
    if (isPlaceholderPrompt({ _id: id, ...payload })) {
      throw new Error(
        'This is a sample template and cannot be saved. Use "Create from template" instead.',
      );
    }

    const body = buildUpdatePayload(payload);

    if (useLocalOnly) {
      const updated = normalizePrompt({ ...body, _id: id });
      const current = getLocalPrompts().map(normalizePrompt);
      if (!current.some((item) => item._id === id)) {
        throw new Error('Prompt not found.');
      }
      const next = current.map((item) => (item._id === id ? updated : item));
      setLocalPrompts(next);
      return { success: true, data: updated };
    }

    if (!isPersistedPromptId(id)) {
      throw new Error('Invalid prompt id. Create a new prompt instead of editing the sample.');
    }

    try {
      const response = await api.put(`/sourcing/ai-prompts/${id}`, body);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        useLocalOnly = true;
        return this.updatePrompt(id, payload);
      }
      throw new Error(extractApiError(error, 'Failed to update prompt.'));
    }
  }

  static async activatePrompt(id) {
    if (isPlaceholderPrompt({ _id: id })) {
      throw new Error(
        'The sample template cannot be activated. Create and save your own prompt first.',
      );
    }

    if (useLocalOnly) {
      const current = getLocalPrompts().map(normalizePrompt);
      if (!current.some((item) => item._id === id)) {
        throw new Error('Prompt not found.');
      }
      const next = current.map((item) => ({
        ...item,
        isActive: item._id === id,
      }));
      setLocalPrompts(next);
      return { success: true };
    }

    if (!isPersistedPromptId(id)) {
      throw new Error('Invalid prompt id.');
    }

    try {
      const response = await api.put(`/sourcing/ai-prompts/${id}`, {
        isActive: true,
      });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        useLocalOnly = true;
        return this.activatePrompt(id);
      }
      throw new Error(extractApiError(error, 'Activation failed.'));
    }
  }

  static async deletePrompt(id) {
    if (isPlaceholderPrompt({ _id: id })) {
      throw new Error('The sample template cannot be deleted.');
    }

    if (useLocalOnly) {
      const current = getLocalPrompts().map(normalizePrompt);
      setLocalPrompts(current.filter((item) => item._id !== id));
      return { success: true };
    }

    if (!isPersistedPromptId(id)) {
      throw new Error('Invalid prompt id.');
    }

    try {
      const response = await api.delete(`/sourcing/ai-prompts/${id}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        useLocalOnly = true;
        return this.deletePrompt(id);
      }
      throw new Error(extractApiError(error, 'Delete failed.'));
    }
  }

  static async getActivePrompt() {
    const { data: all } = await this.getPrompts();
    const active = (all || []).find((item) => item.isActive);
    if (!active || isPlaceholderPrompt(active)) {
      return null;
    }
    return active;
  }

  static getPlaceholderTemplate() {
    return normalizePrompt(DEFAULT_PLACEHOLDER_PROMPTS[0]);
  }
}

export default AiPromptConfigService;
